/**
 * TextFilter — per-column text-search input for the MRT filter row.
 *
 * Routes through the shared `@shared/ui/Input` primitive at size
 * `sm` so the trigger inherits the Tier 0 form-field tokens
 * (height ~30 px, qhs-r-md radius, brand-primary focus ring at
 * 3 px / 12 % alpha) directly from the design system. Behaviour is
 * preserved 1:1 vs. legacy:
 *
 *   - Uncontrolled `<input>` whose value is mirrored to `filterValue`
 *     via the parent. A `useEffect` writes back the value when it
 *     changes externally (e.g. clear-all from the toolbar).
 *   - Enter and every keystroke both commit through `onChange(dataKey,
 *     value, true)` with the instant-filter flag.
 *   - `×` clear button appears only when the input has a value;
 *     clicking it commits an empty value through the same path.
 *   - `disabled={!isFilterable}` falls back to Input's disabled state
 *     (qhs-bg surface, text-3 colour, not-allowed cursor) instead of
 *     the previous transparent-border treatment.
 *
 * Note on the clear button: Input's end-icon slot wraps children in
 * a `qhs-field__icon--end` span with `pointer-events: none` (it's
 * designed for decorative glyphs). The inner `<button>` carries
 * `pointer-events: auto` so it receives clicks normally — a
 * `pointer-events: auto` child overrides a `pointer-events: none`
 * ancestor per CSS spec.
 *
 * The clear glyph is a thin SVG `×` line-stroke that mirrors the
 * one used by `DateField`'s clear button — keeping the two filter
 * types visually identical. We previously rendered MUI's
 * `Cancel` icon (a filled circle with a white X), which read as a
 * heavier, different glyph next to date filters in the same row.
 */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Input from '@shared/ui/Input';
import './index.css';

const CloseSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TextFilter = ({
  filterValue = '',
  dataKey,
  columnData: { isFilterable, startAdornment },
  onChange,
}) => {
  const { tables } = useSelector(state => state.i18n);
  const textInput = useRef(null);

  useEffect(() => {
    if (!textInput.current) return;
    if (filterValue === '') {
      textInput.current.value = '';
    } else if (textInput.current.value === '' && filterValue !== null) {
      textInput.current.value = filterValue;
    }
  }, [filterValue]);

  const handleInputChange = e => {
    if (onChange) {
      onChange(dataKey, e.target.value, true);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleInputChange(e);
    }
  };

  const handleClear = () => onChange?.(dataKey, '');

  return (
    <Input
      size="sm"
      fullWidth
      highlighted={!!filterValue}
      ref={textInput}
      defaultValue={filterValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={isFilterable ? tables.filter : ''}
      disabled={!isFilterable}
      autoComplete="off"
      // Marker for Table's reveal-autofocus: when the filter row is
      // shown, the first enabled text filter receives focus.
      data-qhs-text-filter=""
      startIcon={startAdornment || undefined}
      endIcon={
        textInput?.current?.value ? (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear filter"
            className="qhs-filter-clear-btn"
          >
            {CloseSvg}
          </button>
        ) : undefined
      }
    />
  );
};

TextFilter.propTypes = {
  filterValue: PropTypes.string,
  dataKey: PropTypes.string.isRequired,
  columnData: PropTypes.shape({
    isFilterable: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func,
};

export default TextFilter;
